import { ASSIGNMENT_TYPE } from '@/types/capacityCalendar';
import type { CalendarRow, CapacityAllocationMap, PoAllocation } from '@/types/capacityCalendar';
import type { CustomerMapping } from '@/types/customerMapping';
import { customerLabel, resolveCustomerMapping, UNMAPPED_PO_COLOR } from '@/utils/customerColors';

// One bucket for everything the report cannot put against an account: a PO whose customer
// code has no mapping, and capacity blocked without a PO. Report vocabulary only — nothing
// in the app is renamed, and the two stay distinguishable in `unattributedBreakdown`.
export const UNATTRIBUTED_KEY = 'unattributed';
const UNATTRIBUTED_LABEL = 'Unattributed';

const FULL_PERCENT = 100;

export interface CustomerShare {
  key: string;
  label: string;
  color: string;
  quantity: number;
  // Of scheduled work in `slices`, of that type's planned capacity in `types[].shares`.
  percent: number;
}

export interface CapacityReportTypeRow {
  rowId: string;
  typeCode: string;
  typeLabel: string;
  unit: string | null;
  planned: number;
  scheduled: number;
  unused: number;
  unusedPercent: number;
  shares: CustomerShare[];
}

export interface CapacityReport {
  plannedTotal: number;
  scheduledTotal: number;
  unusedTotal: number;
  unusedPercent: number;
  // Share of scheduled work, for the donut and its legend — unused is deliberately absent.
  // It routinely dwarfs every customer (a 78%-idle week leaves them unreadable slivers), so
  // it is reported as its own figure and inside the per-type bars instead.
  slices: CustomerShare[];
  types: CapacityReportTypeRow[];
  unattributedBreakdown: { unmappedPos: number; withoutPo: number };
}

function percentOf(value: number, total: number): number {
  return total > 0 ? (value / total) * FULL_PERCENT : 0;
}

// Mirrors buildAllocationSegments: a zero-quantity assignment consumes nothing, negative
// remaining (overbooked) contributes no unused capacity, and planned is what the bar draws.
function countedQuantity(po: PoAllocation): number {
  return po.quantity !== 0 ? Math.max(po.quantity, 0) : 0;
}

function bucketFor(po: PoAllocation, mappings: CustomerMapping[]): { key: string; label: string; color: string } {
  if (po.type === ASSIGNMENT_TYPE.manual || !po.poNumber) {
    return { key: UNATTRIBUTED_KEY, label: UNATTRIBUTED_LABEL, color: UNMAPPED_PO_COLOR };
  }
  const mapping = resolveCustomerMapping(po.poNumber, mappings);
  if (!mapping) {
    return { key: UNATTRIBUTED_KEY, label: UNATTRIBUTED_LABEL, color: UNMAPPED_PO_COLOR };
  }
  return { key: mapping.id, label: customerLabel(mapping), color: mapping.color };
}

// Unattributed always sorts last so the accounts read first, however large it grows.
function byQuantityDesc(a: CustomerShare, b: CustomerShare): number {
  if (a.key === UNATTRIBUTED_KEY) return 1;
  if (b.key === UNATTRIBUTED_KEY) return -1;
  return b.quantity - a.quantity;
}

export function buildCapacityReport(
  rows: CalendarRow[],
  allocations: CapacityAllocationMap,
  weekKey: string,
  mappings: CustomerMapping[],
): CapacityReport {
  const totalsByKey = new Map<string, CustomerShare>();
  const unattributedBreakdown = { unmappedPos: 0, withoutPo: 0 };
  let plannedTotal = 0;
  let scheduledTotal = 0;
  let unusedTotal = 0;

  const types = rows.reduce<CapacityReportTypeRow[]>((result, row) => {
    const allocation = allocations[row.id]?.[weekKey];
    if (!allocation) {
      return result;
    }

    const shareByKey = new Map<string, CustomerShare>();
    let scheduled = 0;
    allocation.purchaseOrders.forEach((po) => {
      const quantity = countedQuantity(po);
      if (quantity === 0) {
        return;
      }
      scheduled += quantity;
      const bucket = bucketFor(po, mappings);
      if (bucket.key === UNATTRIBUTED_KEY) {
        const target = po.type === ASSIGNMENT_TYPE.manual || !po.poNumber ? 'withoutPo' : 'unmappedPos';
        unattributedBreakdown[target] += quantity;
      }
      const existing = shareByKey.get(bucket.key);
      shareByKey.set(bucket.key, { ...bucket, percent: 0, quantity: (existing?.quantity ?? 0) + quantity });
      const overall = totalsByKey.get(bucket.key);
      totalsByKey.set(bucket.key, { ...bucket, percent: 0, quantity: (overall?.quantity ?? 0) + quantity });
    });

    const unused = Math.max(allocation.remainedCapacity, 0);
    const planned = scheduled + unused;
    plannedTotal += planned;
    scheduledTotal += scheduled;
    unusedTotal += unused;

    result.push({
      rowId: row.id,
      typeCode: row.typeCode,
      typeLabel: row.typeLabel,
      unit: row.unit,
      planned,
      scheduled,
      unused,
      unusedPercent: percentOf(unused, planned),
      shares: [...shareByKey.values()]
        .map((share) => ({ ...share, percent: percentOf(share.quantity, planned) }))
        .sort(byQuantityDesc),
    });
    return result;
  }, []);

  return {
    plannedTotal,
    scheduledTotal,
    unusedTotal,
    unusedPercent: percentOf(unusedTotal, plannedTotal),
    slices: [...totalsByKey.values()]
      .map((share) => ({ ...share, percent: percentOf(share.quantity, scheduledTotal) }))
      .sort(byQuantityDesc),
    types,
    unattributedBreakdown,
  };
}
